import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export type InteractionKind =
  | 'phone_outbound' | 'phone_inbound'
  | 'email_outbound' | 'email_inbound';

export type InteractionOutcome =
  | 'connected'
  | 'voicemail_left'
  | 'no_answer'
  | 'busy'
  | 'wrong_number'
  | 'client_hung_up'
  | 'callback_scheduled';

export interface InteractionCaseLink {
  case_id: string;
  is_primary: boolean;
  workflow_slug: string;
  status: string;
}

export interface Interaction {
  id: string;
  client_id: string;
  actor_id: string;
  actor_name: string | null;
  kind: InteractionKind;
  outcome: InteractionOutcome;
  next_action_required: string | null;
  commitment_made: string | null;
  notes: string | null;
  is_pinned: boolean;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  correlation_id: string;
  created_at: string;
  cases: InteractionCaseLink[];
}

export interface ClientCase {
  case_id: string;
  workflow_slug: string;
  workflow_name: string;
  current_state_slug: string;
  status: string;
  created_at: string;
}

export interface StartInteractionPayload {
  kind: InteractionKind;
  /** ISO timestamp; server stamps now() if omitted. */
  started_at?: string;
  /** Pass when this is a retry attempt in the same outbound session. */
  correlation_id?: string;
}

export interface UpdateInteractionPayload {
  outcome?: InteractionOutcome;
  ended_at?: string;
  notes?: string;
  next_action_required?: string;
  commitment_made?: string;
}

export interface SpawnAdjacentPayload {
  workflow_slug: string;
  interaction_id?: string;
  spawn_kind?: 'manual' | 'auto';
  notes?: string;
}

export interface ScheduleCallbackPayload {
  /** ISO datetime — must be tz-aware (include the timezone offset). */
  starts_at: string;
  /** Defaults to the scheduling slot length (usually 30 min). */
  duration_minutes?: number;
  /** Pre-filled from `next_action_required` on the UI. */
  purpose?: string;
}

export interface ScheduledCallback {
  id: string;
  agent_id: string | null;
  member_id: string;
  case_id: string | null;
  starts_at: string;
  ends_at: string;
  status: string;
  purpose: string | null;
}

/**
 * Staff-only interactions API. Captures every staff↔client
 * communication attempt and links it to the primary case + any
 * adjacent cases discovered mid-call. Backend RBAC restricts to
 * agent / manager / super_admin; clients never touch this surface.
 */
class InteractionsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  async start(
    primaryCaseId: string,
    payload: StartInteractionPayload,
  ): Promise<{ id: string }> {
    const res = await this.api.post<{ id: string }>(
      `/api/v1/staff/cases/${primaryCaseId}/interactions`,
      payload,
    );
    return res.data;
  }

  async update(
    interactionId: string,
    payload: UpdateInteractionPayload,
  ): Promise<void> {
    await this.api.patch(
      `/api/v1/staff/interactions/${interactionId}`,
      payload,
    );
  }

  /** Soft-delete an abandoned in-flight interaction (started but never wrapped
   *  up). The backend rejects discarding a finalized interaction. */
  async discard(interactionId: string): Promise<void> {
    await this.api.post(`/api/v1/staff/interactions/${interactionId}/discard`, {});
  }

  async linkAdjacentCase(
    interactionId: string,
    caseId: string,
  ): Promise<void> {
    await this.api.post(
      `/api/v1/staff/interactions/${interactionId}/cases/${caseId}`,
    );
  }

  async spawnAdjacentCase(
    primaryCaseId: string,
    payload: SpawnAdjacentPayload,
  ): Promise<{ id: string }> {
    const res = await this.api.post<{ id: string }>(
      `/api/v1/staff/cases/${primaryCaseId}/spawn-adjacent`,
      payload,
    );
    return res.data;
  }

  async listForCase(caseId: string): Promise<Interaction[]> {
    const res = await this.api.get<{ items: Interaction[] }>(
      `/api/v1/staff/cases/${caseId}/interactions`,
    );
    return res.data.items;
  }

  async listForClient(clientId: string, limit = 50): Promise<Interaction[]> {
    const res = await this.api.get<{ items: Interaction[] }>(
      `/api/v1/staff/clients/${clientId}/interactions`,
      { params: { limit } },
    );
    return res.data.items;
  }

  async listOpenCases(clientId: string): Promise<ClientCase[]> {
    const res = await this.api.get<{ items: ClientCase[] }>(
      `/api/v1/staff/clients/${clientId}/cases`,
    );
    return res.data.items;
  }

  async pin(interactionId: string, pinned: boolean): Promise<void> {
    await this.api.patch(
      `/api/v1/staff/interactions/${interactionId}/pin`,
      { pinned },
    );
  }

  async scheduleCallback(
    interactionId: string,
    payload: ScheduleCallbackPayload,
  ): Promise<ScheduledCallback> {
    const res = await this.api.post<ScheduledCallback>(
      `/api/v1/staff/interactions/${interactionId}/schedule-callback`,
      payload,
    );
    return res.data;
  }
}

export default new InteractionsService();
